import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Settings, CheckCircle, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface GoHighLevelIntegration {
  id: string;
  company_id: string;
  api_token: string;
  location_id: string;
  is_active: boolean;
  sync_contacts: boolean;
  sync_opportunities: boolean;
  last_contact_sync: string | null;
  last_opportunity_sync: string | null;
  created_at: string;
  updated_at: string;
  company_name?: string;
}

const GoHighLevelPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<GoHighLevelIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!supabase) throw new Error('Supabase not initialized');

      const { data, error: queryError } = await supabase
        .from('gohighlevel_integrations')
        .select(`
          *,
          sw_companies!inner(name)
        `)
        .order('created_at');

      if (queryError) {
        throw new Error(queryError.message);
      }

      const integrationsWithCompany = data?.map(integration => ({
        ...integration,
        company_name: integration.sw_companies?.name || 'Unknown Company'
      })) || [];

      setIntegrations(integrationsWithCompany);
    } catch (err: any) {
      setError(err.message);
      console.error('Error loading integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateIntegration = async (id: string, updates: Partial<GoHighLevelIntegration>) => {
    try {
      if (!supabase) throw new Error('Supabase not initialized');

      const { error: updateError } = await supabase
        .from('gohighlevel_integrations')
        .update(updates)
        .eq('id', id);

      if (updateError) {
        throw new Error(updateError.message);
      }

      await loadIntegrations();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const testConnection = async (integration: GoHighLevelIntegration) => {
    try {
      console.log('Testing connection for:', integration.location_id);
      await updateIntegration(integration.id, {
        last_contact_sync: new Date().toISOString()
      });
      alert('Connection test successful!');
    } catch (err: any) {
      alert(`Connection test failed: ${err.message}`);
    }
  };

  const syncData = async (integration: GoHighLevelIntegration, type: 'contacts' | 'opportunities') => {
    try {
      console.log(`Syncing ${type} for:`, integration.location_id);
      const updateField = type === 'contacts' ? 'last_contact_sync' : 'last_opportunity_sync';
      await updateIntegration(integration.id, {
        [updateField]: new Date().toISOString()
      });
      alert(`${type} sync initiated successfully!`);
    } catch (err: any) {
      alert(`${type} sync failed: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-900">GoHighLevel Integrations</h1>
          </div>
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-900">GoHighLevel Integrations</h1>
          </div>
          <Card className="border-red-200 bg-red-50">
            <div className="p-6">
              <div className="flex items-center gap-2 text-red-600 mb-4">
                <AlertCircle className="h-4 w-4" />
                <span>Error loading integrations: {error}</span>
              </div>
              <button
                onClick={loadIntegrations}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">GoHighLevel Integrations</h1>
          <button
            onClick={loadIntegrations}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="grid gap-6">
          {integrations.map((integration) => (
            <Card key={integration.id}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Settings className="h-5 w-5 text-gray-500" />
                    <h3 className="text-lg font-medium">{integration.company_name}</h3>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded ${
                    integration.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {integration.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location ID
                    </label>
                    <div className="p-2 bg-gray-50 border rounded text-sm font-mono">
                      {integration.location_id}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Token
                    </label>
                    <div className="p-2 bg-gray-50 border rounded text-sm font-mono">
                      {integration.api_token.substring(0, 20)}...
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Last Contact Sync:</span>
                    <p className="text-gray-600">
                      {integration.last_contact_sync
                        ? new Date(integration.last_contact_sync).toLocaleString()
                        : "Never"
                      }
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Last Opportunity Sync:</span>
                    <p className="text-gray-600">
                      {integration.last_opportunity_sync
                        ? new Date(integration.last_opportunity_sync).toLocaleString()
                        : "Never"
                      }
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => testConnection(integration)}
                    disabled={!integration.is_active}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Test Connection
                  </button>

                  {integration.sync_contacts && (
                    <button
                      onClick={() => syncData(integration, 'contacts')}
                      disabled={!integration.is_active}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Sync Contacts
                    </button>
                  )}

                  {integration.sync_opportunities && (
                    <button
                      onClick={() => syncData(integration, 'opportunities')}
                      disabled={!integration.is_active}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Sync Opportunities
                    </button>
                  )}

                  <button
                    onClick={() => window.open(`https://app.gohighlevel.com/location/${integration.location_id}`, '_blank')}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in GHL
                  </button>
                </div>
              </div>
            </Card>
          ))}

          {integrations.length === 0 && (
            <Card>
              <div className="p-12 text-center">
                <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No GoHighLevel integrations found.</p>
                <p className="text-sm text-gray-500">
                  GoHighLevel integrations are automatically created when companies are configured.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoHighLevelPage;